/* eslint-disable react/jsx-no-useless-fragment */
import { APP_ENV, CategoryItemsProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import OutOfStock from '../../OutOfStock';
import { getScaleSize, isBetweenRange } from '../../../../../utils/utils';
import { TEMPLATE_1_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface Item extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

interface ItemProps {
  template: Template;
  item: Item;
  lastItemIndex: number;
  index?: number;
  development?: boolean;
  timezone?: string;
  scale?: number;
  hasAnyModifier: boolean;
  tenantId?: number;
  column2FirstChild: string | null;
  categoryId: string;
}

const Item = ({
  template,
  item,
  lastItemIndex,
  index,
  development,
  timezone,
  scale = 1,
  hasAnyModifier,
  tenantId,
  categoryId,
  column2FirstChild,
}: ItemProps) => {
  const { normalizePixels } = useNormalizedPixels();
  const isDevelopment = development ? true : isBetweenRange(item.schedules, timezone);
  const isItemPriceEnabled = template?.itemPrice?.isEnabled;
  const itemTextScale = template?.item?.textScale ?? 1;
  const media = template?.media?.mediaContent;
  const templateLayout = template.layoutType;
  const isMedia = template.media?.isEnabled && media?.url;
  const itemId = `item-${item.id}-${categoryId}`;
  const hasMargin = column2FirstChild === itemId;
  //const fontFamily = extractFontFamilyFromUrl(template?.fontFamily);
  const fontFamily = template?.fontFamilyName;

  const style = {
    container: {
      alignSelf: 'start',
      height: 'min-content',
      overflow: 'hidden',
    },
    itemNameContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    itemName: {
      fontWeight: '600',
      fontFamily: fontFamily,
      '-webkit-line-clamp': '1',
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    itemPrice: {
      textAlign: 'center' as const,
      fontFamily: fontFamily,
      lineHeight: 'normal',
      fontWeight: '600',
    },
    itemDesc: {
      fontWeight: '400',
      fontFamily: fontFamily,
      display: '-webkit-box',
      '-webkit-line-clamp': '1',
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
    },
  };

  return (
    <>
      {isDevelopment && (
        <BoxComponent
          id={itemId}
          sx={{
            width: isMedia && templateLayout === 1 ? '48%' : '32%',
            ...style.container,
            ...(lastItemIndex === index && isItemPriceEnabled
              ? { marginBottom: getScaleSize(normalizePixels(24, 'height'), scale) }
              : {}),
            ...(hasMargin && isMedia && templateLayout === 2
              ? { marginTop: getScaleSize(normalizePixels(530, 'height'), scale) }
              : {}),
          }}
        >
          <BoxComponent sx={style.itemNameContainer}>
            <BoxComponent
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                flex: 1,
                minWidth: 0,
              }}
            >
              <TypographyComponent
                sx={{
                  ...style.itemName,
                  color: template.item.itemName.color ? template.item.itemName.color : '#FFFEFE',
                  minWidth: 0,
                  fontWeight: template.item.itemName.fontWeight,
                  fontStyle: template.item.itemName.fontStyle,
                  letterSpacing: template.item.itemName.letterSpacing,
                  lineHeight: template.item.itemName.lineHeight,
                  fontSize: getScaleSize(normalizePixels(22.5 * itemTextScale, 'height'), scale),
                  ...(item.outOfStock ? { opacity: '30%' } : {}),
                }}
              >
                {item.itemName}
              </TypographyComponent>
              {item?.outOfStock && <OutOfStock scale={scale} fontFamily={fontFamily} />}
            </BoxComponent>

            {isItemPriceEnabled && item?.hasValidModifiers && (
              <BoxComponent
                sx={{
                  flexShrink: 0,
                  flexGrow: 0,
                  ml: 1,
                  width: `${35 * itemTextScale}% !important`,
                }}
              >
                {item.hasValidModifiers && (
                  <GridComponent container columnSpacing={1}>
                    {TEMPLATE_1_MODIFIERS.map((size) => (
                      <GridComponent key={size} item size={6}>
                        <TypographyComponent
                          sx={{
                            ...style.itemPrice,
                            color: template.item?.itemPrice?.color ? template.item?.itemPrice?.color : '#FFFEFE',
                            fontWeight: template.item.itemPrice.fontWeight,
                            fontStyle: template.item.itemPrice.fontStyle,
                            letterSpacing: template.item.itemPrice.letterSpacing,
                            lineHeight: template.item.itemPrice.lineHeight,
                            fontSize: getScaleSize(normalizePixels(22.5 * itemTextScale, 'height'), scale),
                            ...(item.outOfStock ? { opacity: '30%' } : {}),
                          }}
                        >
                          {item.modifiers && item?.modifiers[size]
                            ? process.env?.NX_NODE_ENV === APP_ENV.UAT && tenantId === 2971
                              ? Number(item?.modifiers[size])
                              : Number(item?.modifiers[size]).toFixed(2)
                            : '-'}
                        </TypographyComponent>
                      </GridComponent>
                    ))}
                  </GridComponent>
                )}
              </BoxComponent>
            )}

            {(!hasAnyModifier || !item.hasValidModifiers) && isItemPriceEnabled && (
              <TypographyComponent
                sx={{
                  ...style.itemPrice,
                  flexShrink: 0,
                  flexGrow: 0,
                  // width: `${22 * itemTextScale}% !important`,
                  color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#FFFEFE',
                  fontSize: getScaleSize(normalizePixels(22.5 * itemTextScale, 'height'), scale),
                  fontWeight: template.item.itemPrice.fontWeight,
                  fontStyle: template.item.itemPrice.fontStyle,
                  letterSpacing: template.item.itemPrice.letterSpacing,
                  lineHeight: template.item.itemPrice.lineHeight,

                  ...(item.outOfStock ? { opacity: '30%' } : {}),
                }}
              >
                {Number(item.itemPrice).toFixed(2)}
              </TypographyComponent>
            )}
          </BoxComponent>

          {template?.itemDescription && item.itemDescription && template?.itemDescription.isEnabled && (
            <TypographyComponent
              sx={{
                ...style.itemDesc,
                color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#FFFEFE',
                width: isItemPriceEnabled ? (item?.hasValidModifiers ? '65%' : '80%') : '100%',
                fontSize: getScaleSize(normalizePixels(15 * itemTextScale, 'height'), scale),
                fontWeight: template.item.itemDescription?.fontWeight,
                fontStyle: template.item.itemDescription?.fontStyle,
                letterSpacing: template.item.itemDescription?.letterSpacing,
                lineHeight: template.item.itemDescription?.lineHeight,
                ...(item.outOfStock ? { opacity: '30%' } : {}),
              }}
            >
              {item.itemDescription}
            </TypographyComponent>
          )}
        </BoxComponent>
      )}
    </>
  );
};

export default Item;
